import { useEffect, useRef } from "react";
import { hash32 } from "../lib/hash";

// CosmicBackdrop — OrbitLens's signature generative brand backdrop. PR 1 of the
// "Generative Identity" visual system. A flowing, painterly aurora rendered on a
// WebGL fragment shader — the OrbitLens translation of Stripe's silky gradient
// art into our own language: warm-dark, telescope-cosmology, observation-not-
// decoration.
//
// WHY WEBGL: the silk must OOZE smoothly ("ぬらーっと") at 60fps. An animated SVG
// turbulence filter recomputes per frame and janks; a fragment shader runs on
// the GPU and stays buttery. The flow is domain-warped fractal noise (IQ-style
// fbm of fbm) animated by time — the field never rotates or slides (that read as
// "the background spinning"), it churns IN PLACE. A radial term melts the aurora
// into the THEME background in every direction (no edge — dark dissolves to the
// void, light dissolves to the ivory page); a calm pocket lowers presence at the
// centre so the sign-in card never sits on busy flow.
//
// CONSTRAINTS: colours are read at runtime from the design TOKENS (--gold etc.)
// so it stays theme-sourced (no hex literals here) — and the whole layer is
// scaled by --backdrop-intensity (chrome) / --backdrop-intensity-feature (login),
// with light strength dialled down to a Stripe-soft pastel wash. Seeded by
// IDENTITY (the galaxy login), never by scores. feature variant animates; chrome
// is a single
// static frame behind app data; motion is gated off under prefers-reduced-motion
// and while the tab is hidden. If WebGL is unavailable it degrades to a static
// CSS gradient.

type Props = {
  seed?: string;
  variant?: "chrome" | "feature";
  className?: string;
  // Optional overrides for embedded use (e.g. the ambient wall canvas) where the
  // variant defaults are too strong. `strength` overrides the shader amplitude
  // (variant default otherwise); `intensity` overrides the wrapper opacity (the
  // --backdrop-intensity* token otherwise). Both undefined ⇒ original behaviour.
  strength?: number;
  intensity?: number;
};

const VERT = `
attribute vec2 a;
varying vec2 v_uv;
void main(){ v_uv = vec2(a.x*0.5+0.5, 0.5-a.y*0.5); gl_Position = vec4(a,0.0,1.0); }
`;

const FRAG = `
precision highp float;
precision highp int;
varying vec2 v_uv;
uniform vec2 u_res;
uniform float u_time;
uniform float u_seed;
uniform float u_strength;
uniform vec3 u_bg;
uniform vec3 c0,c1,c2,c3,c4;
float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453123); }
float vnoise(vec2 p){
  vec2 i=floor(p), f=fract(p);
  float a=hash(i), b=hash(i+vec2(1.,0.)), c=hash(i+vec2(0.,1.)), d=hash(i+vec2(1.,1.));
  vec2 u=f*f*(3.-2.*f);
  return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
}
float fbm(vec2 p){
  float s=0., a=0.55;
  for(int i=0;i<5;i++){ s+=a*vnoise(p); p=p*2.02+vec2(1.7,9.2); a*=0.5; }
  return s;
}
vec3 ramp(float x){
  x=clamp(x,0.,1.);
  vec3 col=c0;
  col=mix(col,c1,smoothstep(0.0,0.28,x));
  col=mix(col,c2,smoothstep(0.22,0.5,x));
  col=mix(col,c3,smoothstep(0.46,0.74,x));
  col=mix(col,c4,smoothstep(0.7,1.0,x));
  return col;
}
void main(){
  vec2 uv=v_uv;
  vec2 asp=vec2(u_res.x/u_res.y, 1.0);
  vec2 p=(uv-0.5)*asp*2.2 + u_seed;
  float t=u_time*0.05;
  vec2 q=vec2(fbm(p+t), fbm(p+vec2(5.2,1.3)-t));
  vec2 r=vec2(fbm(p+1.8*q+vec2(1.7,9.2)+0.7*t), fbm(p+1.8*q+vec2(8.3,2.8)-0.6*t));
  float f=fbm(p+2.2*r);
  float hpos = uv.x*0.85 + (f-0.5)*0.7 + (r.x-0.5)*0.35;
  vec3 col=ramp(hpos);
  col *= 0.7 + 0.6*f;
  vec2 cc=(uv-vec2(0.5,0.44))*asp;
  float d=length(cc);
  float melt=1.0 - smoothstep(0.34,0.78,d);
  float calm=smoothstep(0.0,0.36,d);
  // Melt the aurora into the THEME background (u_bg), not hard black. The calm
  // pocket lowers presence at centre so the sign-in card sits on quiet field.
  // Dark theme: u_bg ≈ void → mix collapses to col*a (the original look, now
  // edge-matched to the page void instead of pure black). Light theme: u_bg ≈
  // near-white → the same field reads as a soft luminous wash that dissolves
  // into the ivory page, never a dark hole.
  float a = melt*u_strength*mix(0.5,1.0,calm);
  col = mix(u_bg, col, a);
  float st=hash(floor(uv*asp*420.0));
  col+=step(0.9975,st)*0.45*melt;
  gl_FragColor=vec4(col,1.0);
}
`;

// readColor resolves a design token (any var() chain) to linear-ish [r,g,b] in
// 0..1 by letting the browser compute it — so the shader uses the SAME palette
// as the rest of the app, with no hex literal in this file.
function readColor(name: string): [number, number, number] {
  const probe = document.createElement("span");
  probe.style.color = `var(${name})`;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const m = getComputedStyle(probe).color.match(/[\d.]+/g);
  probe.remove();
  if (!m) return [1, 1, 1];
  return [Number(m[0]) / 255, Number(m[1]) / 255, Number(m[2]) / 255];
}

function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.error("CosmicBackdrop shader compile failed:", gl.getShaderInfoLog(sh));
  }
  return sh;
}

export function CosmicBackdrop({ seed = "orbitlens", variant = "chrome", className, strength, intensity }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const gl = cv.getContext("webgl", { antialias: false, alpha: false, premultipliedAlpha: false });
    if (!gl) {
      // Graceful fallback: a static token gradient so it's never blank.
      cv.style.background =
        "radial-gradient(60% 60% at 35% 40%, color-mix(in oklab, var(--gold) 28%, transparent), transparent 70%), var(--bg-void)";
      return;
    }

    const prog = gl.createProgram()!;
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      // Never leave a blank canvas: log why and degrade to a static gradient.
      console.error("CosmicBackdrop program link failed:", gl.getProgramInfoLog(prog));
      cv.style.background =
        "radial-gradient(60% 60% at 35% 40%, color-mix(in oklab, var(--gold) 28%, transparent), transparent 70%), var(--bg-void)";
      gl.getExtension("WEBGL_lose_context")?.loseContext();
      return;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "a");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const U = (n: string) => gl.getUniformLocation(prog, n);
    const uRes = U("u_res"), uTime = U("u_time"), uStrength = U("u_strength");
    gl.uniform1f(U("u_seed"), (hash32(seed) % 1000) / 50);
    const names = ["--gold-bright", "--gold", "--gold-dim", "--gb-purple", "--gb-blue"];
    names.forEach((nm, i) => gl.uniform3fv(U(`c${i}`), readColor(nm)));
    // Melt target = the live theme background, so the field dissolves into the
    // page (dark void / light ivory) seamlessly rather than into hard black.
    gl.uniform3fv(U("u_bg"), readColor("--bg-void"));

    const isLight = typeof document !== "undefined" && document.documentElement.classList.contains("light");
    const feature = variant === "feature";
    const reduce = typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const animate = feature && !reduce;
    // Light theme melts toward near-white, so hold strength a touch under the
    // dark full strength for a rich-but-clean wash (the warm tints come from the
    // cleaned-up amber gold tokens — burnt brown would read dusty here).
    gl.uniform1f(uStrength, strength ?? (feature ? (isLight ? 0.72 : 1.0) : 0.4));
    gl.clearColor(0, 0, 0, 1);

    // Non-null typed captures: TS does not preserve the `if (!x) return`
    // narrowing of cv/gl inside the nested resize()/draw() closures, so bind
    // them to consts with concrete (non-null) types here.
    const el: HTMLCanvasElement = cv;
    const g: WebGLRenderingContext = gl;

    const dpr = () => Math.min(window.devicePixelRatio || 1, 1.5);
    function resize() {
      const w = Math.max(1, Math.round(el.clientWidth * dpr()));
      const h = Math.max(1, Math.round(el.clientHeight * dpr()));
      if (el.width !== w || el.height !== h) {
        el.width = w;
        el.height = h;
        g.viewport(0, 0, w, h);
      }
      g.uniform2f(uRes, el.width, el.height);
    }

    let raf = 0;
    const start = performance.now();
    function draw(now: number) {
      resize();
      g.uniform1f(uTime, animate ? (now - start) / 1000 : 0);
      g.clear(g.COLOR_BUFFER_BIT);
      g.drawArrays(g.TRIANGLES, 0, 3);
      if (animate && document.visibilityState === "visible") raf = requestAnimationFrame(draw);
      else raf = 0;
    }
    draw(start);

    const onVis = () => {
      if (animate && document.visibilityState === "visible" && !raf) raf = requestAnimationFrame(draw);
    };
    document.addEventListener("visibilitychange", onVis);
    const ro = new ResizeObserver(() => {
      if (!raf) draw(performance.now()); // redraw static frame on resize
    });
    ro.observe(cv);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVis);
      ro.disconnect();
      // NB: do NOT loseContext() here. React StrictMode (dev) runs the effect
      // mount→unmount→mount; losing the context on the first unmount leaves the
      // re-mount compiling shaders against a dead context ("compile failed:
      // null", even for the trivial vertex shader). Letting GC reclaim it means
      // the re-mount reuses the same live context via getContext().
    };
  }, [seed, variant, strength]);

  const isLightRender = typeof document !== "undefined" && document.documentElement.classList.contains("light");
  // Grain dithers banding on the dark theme, but soft-light grain over the light
  // ivory reads "sandy" — drop it to a whisper there.
  const grainOpacity = isLightRender ? 0.012 : variant === "chrome" ? 0.035 : 0.06;
  // The chrome backdrop behind app data stays near-invisible in light (so it
  // never muddies the ivory data screens); the feature backdrop (sign-in) gets
  // its own knob so the brand aurora is fully present on the login page.
  const intensityVar =
    intensity != null
      ? String(intensity)
      : variant === "feature"
        ? "var(--backdrop-intensity-feature)"
        : "var(--backdrop-intensity)";

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden ${className ?? ""}`}
      style={{ background: "var(--bg-void)", opacity: intensityVar }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 block h-full w-full" />

      {/* Fine grain over the shader — dithers any residual banding + premium
          matte. Static SVG turbulence; soft-light keeps it subtle. */}
      <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" focusable="false" style={{ mixBlendMode: "soft-light", opacity: grainOpacity }}>
        <filter id="cb-grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.82" numOctaves={2} stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#cb-grain)" />
      </svg>
    </div>
  );
}
